import Guard from 'arg-guard'

/**
 * Definition of a segment keymap entry.
 *
 * Maps provided API keys for segments to images and / or icons to display.
 *
 * @export {KeymapEntry} An entry for the segment keymap.
 * @class KeymapEntry
 */
export default class KeymapEntry {

    /**
     * Creates a keymap entry.
     *
     * The Default panel will prettify any generic JSON object data for display.
     * If creating a custom panel that is editable then an EditMode property that points at the editable custom
     * component must be present as in SegmentInfoPanels.Default.
     *
     * @param {string} key The lookup key provided by the API.
     * @param {string} text The display text to use for the segment.
     * @param {?string} faIcon An optional font awesome icon to use for the icon.
     * @param {*|null} svg An optional custom SVG icon to use. These should be made into Vue components so they can be rendered inline.
     * @param {?string} image An optional image to use as an icon alternative.
     * @param {string} buttonText Optional button text to use on navigation buttons instead of the full text value.
     * @param {string} displayAs Used only if 'image' is specified ['icon' or 'image'] (If 'icon' then image should have a transparent background and will be used like an icon, If 'image' or not provided then the image will be used as the background).
     * @param {Vue} component The component object to render the info into. If not provided, SegmentInfoPanels.Default will be used.
     */
    constructor(key, text, {
        faIcon = null, svg = null, image = null,
        buttonText = text, displayAs = 'image' } = {
        faIcon: null, svg: null, image: null, buttonText: text, displayAs: 'image',
    }, component = null) {
        Guard.throwIf(key, 'key')
        Guard.throwIf(text, 'text')

        if (faIcon == null && svg == null && image == null)
            throw new TypeError('A valid KeyMapEntry requires either the faIcon, svg or image properties to be set.')

        /**
         * The lookup key provided by the API.
         * @type {string}
         */
        this.key = key

        /**
         * The display text to use for the segment.
         * @type {string}
         */
        this.text = text

        /**
         The display text to use for the segment buttons.
         * @type {string}
         */
        this.buttonText = buttonText !== null ? buttonText : text

        if (faIcon !== null) {
            /**
             * The font awesome icon to use for the icon.
             * @type {string}
             */
            this.icon = faIcon
        }

        if (svg !== null) {
            /**
             * The custom SVG icon to use.
             *
             * These are made into Vue components so they can be rendered inline.
             * @type {*}
             */
            this.svg = svg
        }

        if (image !== null) {
            /**
             * The image to use as an icon alternative.
             * @type {string}
             */
            this.image = image

            /**
             * Specified how an image is used.
             *
             * If 'icon' then image should have a transparent background and will be used like an icon.
             *
             * If 'image' or not provided then the image will be used as the background.
             * @type {string}
             */
            this.displayAs = displayAs
        }

        if (component !== null) {
            /**
             * The component object to render the info into, when not using SegmentInfoPanels.Default.
             * @type {Vue}
             */
            this.component = component
        }
    }
}
